import { UIKit } from "@/shared/ui";
/**
 * Глобальный перехватчик ошибок рендера.
 *
 * React hook error вроде «Rendered fewer hooks than expected» ломает дерево
 * рендера, но не должен убивать всё приложение целиком. Boundary показывает
 * диагностическое сообщение и сохраняет стек в window для отладки.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

declare global {
	interface Window {
		LAST_RENDER_ERROR?: {
			error: Error;
			componentStack: string;
		};
	}
}

type AppErrorBoundaryProps = {
	children: ReactNode;
	fallback?: ReactNode;
};

type AppErrorBoundaryState = {
	hasError: boolean;
	error?: Error;
};

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
	state: AppErrorBoundaryState = { hasError: false };

	static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const componentStack = errorInfo.componentStack ?? "";
		console.error("[AppErrorBoundary] Render error", error);
		console.error(componentStack);

		if (typeof window !== "undefined") {
			window.LAST_RENDER_ERROR = {
				error,
				componentStack,
			};
		}
	}

	render() {
		if (this.state.hasError) {
			return (
				this.props.fallback ?? (
					<div className="text-foreground grid min-h-screen place-items-center bg-surface px-6 text-center">
						<div className="max-w-md rounded-2xl border border-border bg-surface-raised p-8 shadow-xl">
							<UIKit.Text className="mb-2 text-xs font-medium tracking-display text-danger uppercase">
								Ошибка рендера
							</UIKit.Text>
							<UIKit.Text className="mb-3 text-2xl font-semibold">
								Не удалось отрисовать интерфейс
							</UIKit.Text>
							<UIKit.Text className="mb-5 text-sm leading-copy-relaxed text-ink">
								В компоненте произошёл критический сбой. Проверьте консоль браузера и исходники
								трассировки — она сейчас сохраняется в window.LAST_RENDER_ERROR.
							</UIKit.Text>
							<button
								type="button"
								className="rounded-lg border-none bg-accent px-4 py-2 text-sm font-medium text-overlay-ink"
								onClick={() => window.location.reload()}
							>
								Перезагрузить страницу
							</button>
						</div>
					</div>
				)
			);
		}

		return this.props.children;
	}
}
